import React, { useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, Flame } from "lucide-react";
import CoinLogo from "@/components/CoinLogo";
import BondingCurveBar from "@/components/BondingCurveBar";
import VerifiedCheck from "@/components/VerifiedCheck";
import { shortAddr } from "@/lib/api";
import { fmtSui, fmtUsd, timeAgo } from "@/lib/format";
import { isTouch } from "@/lib/perf";

export default function CoinRow({ coin, suiUsd = null }) {
  const navigate = useNavigate();
  const mcUsd = suiUsd ? coin.market_cap_sui * suiUsd : null;
  const vol24 = coin.vol_24h_sui || 0;
  const hot = (coin.buys_1h || 0) > 0;

  const rowRef = useRef(null);
  const prevSui = useRef(undefined);
  const animRef = useRef(null);

  // Same real-time buy/sell flash as the grid card.
  useEffect(() => {
    const cur = coin.real_sui_collected;
    const prev = prevSui.current;
    prevSui.current = cur;
    if (isTouch) return;
    if (prev === undefined || cur === prev || typeof cur !== "number") return;
    const el = rowRef.current;
    if (!el || !el.animate) return;
    const glow = cur > prev ? "rgba(0,255,135,0.5)" : "rgba(255,59,48,0.5)";
    if (animRef.current) animRef.current.cancel();
    animRef.current = el.animate(
      [
        { boxShadow: "0 0 0px rgba(0,0,0,0)" },
        { boxShadow: `0 0 18px ${glow}`, offset: 0.25 },
        { boxShadow: "0 0 0px rgba(0,0,0,0)" },
      ],
      { duration: 800, easing: "ease-out" }
    );
  }, [coin.real_sui_collected]);

  return (
    <div
      ref={rowRef}
      data-testid={`coin-row-${coin.id || coin.curve_id}`}
      onClick={() => navigate(`/coin/${coin.id || coin.curve_id}`)}
      className="group relative cursor-pointer rounded-xl border border-white/8 bg-gradient-to-r from-white/[0.04] to-transparent px-3 py-3 transition-all duration-200 hover:border-[#1EC62E]/40 hover:shadow-[0_6px_28px_-14px_rgba(0,255,135,0.4)]"
    >
      <div className="flex items-center gap-3">
        <div className="relative shrink-0">
          <CoinLogo coin={coin} size={44} />
          {hot && (
            <span className="absolute -top-1.5 -right-1.5 flex items-center gap-0.5 px-1 py-0.5 rounded-full bg-[#FF6B00] text-black text-[7px] font-bold shadow-lg">
              <Flame className="w-2 h-2" /> HOT
            </span>
          )}
        </div>

        {/* Name / symbol / creator */}
        <div className="min-w-0 w-[150px] sm:w-[190px] shrink-0">
          <div className="flex items-center gap-1.5">
            <span className="font-display font-bold text-sm truncate">{coin.name}</span>
            {coin.verified && <VerifiedCheck size={13} />}
            <span className="font-mono text-xs text-[#1EC62E] shrink-0">${coin.symbol}</span>
            {coin.migrated && (
              <span className="shrink-0 text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded text-black" style={{ background: "linear-gradient(90deg,#00E5FF,#FF007A)" }}>Cetus</span>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground font-mono mt-0.5 truncate">
            by <span onClick={(e) => { e.stopPropagation(); navigate(`/u/${coin.creator}`); }} className="hover:text-[#1EC62E] cursor-pointer">{shortAddr(coin.creator)}</span> · {timeAgo(coin.created_at)}
          </div>
        </div>

        {/* Progress bar (flex-grow) */}
        <div className="hidden md:block flex-1 min-w-[120px]">
          <BondingCurveBar
            progress={coin.progress}
            migrated={coin.migrated}
            current={coin.real_sui_collected}
            remaining={coin.sui_remaining}
            gradSui={coin.graduation_sui || 7500}
            showLabel
          />
        </div>

        {/* Market cap */}
        <div className="flex flex-col items-end w-[80px] shrink-0 ml-auto md:ml-0">
          <span className="text-[9px] text-muted-foreground uppercase tracking-wide">Mkt cap</span>
          <span className="font-mono font-bold text-white text-sm">{mcUsd == null ? "—" : fmtUsd(mcUsd)}</span>
        </div>

        {/* 24h vol */}
        <div className="hidden sm:flex flex-col items-end w-[92px] shrink-0">
          <span className="text-[9px] text-muted-foreground uppercase tracking-wide">24h vol</span>
          <span className="font-mono text-xs text-[#1EC62E]">{fmtSui(vol24, 2)} SUI</span>
        </div>

        {/* Replies */}
        <div className="hidden lg:flex items-center gap-1 text-muted-foreground text-xs w-[44px] shrink-0 justify-end">
          <MessageCircle className="w-3.5 h-3.5" /> {coin.reply_count || 0}
        </div>
      </div>
    </div>
  );
}
